import type { ThreadHistoryAdapter } from "@assistant-ui/react";
import {
  agentAssistantMessageMetadataToCustom,
  normalizeAgentAssistantMessageMetadata,
  type AgentCurrentUserFeedback,
} from "@/api/client";
import { create } from "zustand";

type StoredMessage = Parameters<ThreadHistoryAdapter["append"]>[0];
type StoredFeedback = AgentCurrentUserFeedback | null | undefined;

interface ThreadChat {
  sessionId: string | null;
  messages: StoredMessage[];
  lastActiveAt: number;
}

interface AskFocalHistoryState {
  chats: Record<string, ThreadChat>;
  getChat: (threadKey: string) => ThreadChat | undefined;
  getMessageFeedback: (threadKey: string, messageId: string) => StoredFeedback;
  appendMessage: (threadKey: string, item: StoredMessage) => void;
  updateMessageFeedback: (
    threadKey: string,
    messageId: string,
    feedback: AgentCurrentUserFeedback | null,
  ) => void;
  setSessionId: (threadKey: string, sessionId: string) => void;
  clearChat: (threadKey: string) => void;
}

const hasCurrentUserFeedback = (
  metadata: ReturnType<typeof normalizeAgentAssistantMessageMetadata>,
): boolean => {
  return Boolean(
    metadata &&
    Object.prototype.hasOwnProperty.call(metadata, "current_user_feedback"),
  );
};

const getStoredMessageFeedback = (
  messages: StoredMessage[],
  messageId: string,
): StoredFeedback => {
  for (const item of messages) {
    if (item.message.role !== "assistant") continue;

    const metadata = normalizeAgentAssistantMessageMetadata(item.message);
    if (metadata?.message_id !== messageId) continue;

    return hasCurrentUserFeedback(metadata)
      ? (metadata.current_user_feedback ?? null)
      : undefined;
  }

  return undefined;
};

const withMessageFeedback = (
  item: StoredMessage,
  messageId: string,
  feedback: AgentCurrentUserFeedback | null,
): StoredMessage => {
  if (item.message.role !== "assistant") return item;

  const metadata = normalizeAgentAssistantMessageMetadata(item.message);
  if (metadata?.message_id !== messageId) return item;

  const currentUserFeedback = feedback
    ? {
        ...feedback,
        message_id: feedback.message_id ?? messageId,
        ...(feedback.session_id
          ? { session_id: feedback.session_id }
          : metadata.session_id
            ? { session_id: metadata.session_id }
            : {}),
      }
    : null;
  const nextMetadata = {
    message_id: currentUserFeedback?.message_id ?? metadata.message_id,
    ...(currentUserFeedback?.session_id
      ? { session_id: currentUserFeedback.session_id }
      : metadata.session_id
        ? { session_id: metadata.session_id }
        : {}),
    current_user_feedback: currentUserFeedback,
  };

  return {
    ...item,
    message: {
      ...item.message,
      metadata: {
        ...item.message.metadata,
        custom: {
          ...(item.message.metadata?.custom ?? {}),
          ...agentAssistantMessageMetadataToCustom(nextMetadata),
        },
      },
    },
  };
};

// In-memory only — chat history is intentionally NOT persisted. It exists
// only while the user is on the page that opened the thread; the wrapper's
// unmount cleanup clears it. A page reload (or a reprocess) therefore starts
// a fresh chat rather than rehydrating a stale one.
export const useAskFocalHistoryStore = create<AskFocalHistoryState>(
  (set, get) => ({
    chats: {},

    getChat: (threadKey: string) => {
      return get().chats[threadKey];
    },

    getMessageFeedback: (threadKey: string, messageId: string) => {
      const chat = get().chats[threadKey];
      return chat
        ? getStoredMessageFeedback(chat.messages, messageId)
        : undefined;
    },

    appendMessage: (threadKey: string, item: StoredMessage) => {
      set((state) => {
        const existing = state.chats[threadKey];
        const updated: ThreadChat = {
          sessionId: existing?.sessionId ?? null,
          messages: [...(existing?.messages ?? []), item],
          lastActiveAt: Date.now(),
        };

        return {
          chats: { ...state.chats, [threadKey]: updated },
        };
      });
    },

    updateMessageFeedback: (
      threadKey: string,
      messageId: string,
      feedback: AgentCurrentUserFeedback | null,
    ) => {
      set((state) => {
        const existing = state.chats[threadKey];
        if (!existing) return state;

        const messages = existing.messages.map((item) =>
          withMessageFeedback(item, messageId, feedback),
        );

        return {
          chats: {
            ...state.chats,
            [threadKey]: {
              ...existing,
              messages,
            },
          },
        };
      });
    },

    setSessionId: (threadKey: string, sessionId: string) => {
      set((state) => {
        const existing = state.chats[threadKey];
        return {
          chats: {
            ...state.chats,
            [threadKey]: {
              sessionId,
              messages: existing?.messages ?? [],
              lastActiveAt: existing?.lastActiveAt ?? Date.now(),
            },
          },
        };
      });
    },

    clearChat: (threadKey: string) => {
      set((state) => {
        const { [threadKey]: _, ...rest } = state.chats;
        return { chats: rest };
      });
    },
  }),
);
